import { useQuery } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { FolderKanban, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchProjects } from '../../api/ops';
import { deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { formatNumber } from '../../ui/format';

/**
 * Map filter by project (U20, user request 2026-10-21): a button in the top-right corner, styled like *Layers*, with
 * the visible projects grouped by client and the number of their devices on the map. The choice only filters the
 * device layer (MapLibre layer filter); the source and the realtime path are untouched.
 */
export function ProjectFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (projectId: string, project: api.Project | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const projects = useQuery({
    queryKey: ['projects', null],
    queryFn: () => fetchProjects(),
    staleTime: 60_000,
  });
  const version = useStaticVersion((s) => s.version);
  // devices on the map per project (positioned devices of the registry the user sees)
  const onMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of deviceStatic.values())
      if (d.projectId && d.lon !== undefined) m.set(d.projectId, (m.get(d.projectId) ?? 0) + 1);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the Map is mutable; version marks its changes
  }, [version]);
  const items = useMemo(
    () => (projects.data?.items ?? []).filter((p) => onMap.has(p.id) || p.id === value),
    [projects.data, onMap, value],
  );
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? items.filter((p) => `${p.client_name} ${p.name}`.toLowerCase().includes(needle))
    : items;
  const clients = [...new Set(shown.map((p) => p.client_name))];
  const current = items.find((p) => p.id === value) ?? null;
  const pick = (p: api.Project | null) => {
    onChange(p?.id ?? '', p);
    setOpen(false);
  };
  if (!items.length) return null;
  return (
    <div className="map-tools map-tools-right">
      <button
        type="button"
        className="btn map-tool-btn"
        aria-pressed={Boolean(value)}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FolderKanban size={15} />
        <span className="ellipsis project-filter-label">
          {current ? current.name : t('mapProjects.button')}
        </span>
      </button>
      {open && (
        <div className="overlay-panel project-filter-panel" role="group" aria-label={t('mapProjects.button')}>
          {items.length > 8 && (
            <label className="filter-search">
              <Search size={14} className="muted" />
              <input
                className="input"
                value={q}
                placeholder={t('mapProjects.search')}
                aria-label={t('mapProjects.search')}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
          )}
          <div className="project-filter-list">
            <label className="check-row">
              <input type="radio" name="map-project" checked={!value} onChange={() => pick(null)} />
              {t('mapProjects.all')}
            </label>
            {clients.map((c) => (
              <div key={c} className="project-filter-group">
                {clients.length > 1 && <div className="muted small project-filter-client">{c}</div>}
                {shown
                  .filter((p) => p.client_name === c)
                  .map((p) => (
                    <label key={p.id} className="check-row">
                      <input
                        type="radio"
                        name="map-project"
                        checked={value === p.id}
                        onChange={() => pick(p)}
                      />
                      <span className="ellipsis">{p.name}</span>
                      <span className="muted small">({formatNumber(onMap.get(p.id) ?? 0)})</span>
                    </label>
                  ))}
              </div>
            ))}
            {shown.length === 0 && <span className="muted small">{t('mapProjects.none')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
